import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Drawer, Select, toast } from "@outpave/ds";
import Button from "@mui/material/Button";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { v4 as uuidv4 } from "uuid";
import { TRANSFER_FUNDS } from "../../../graphql/account";
import transferFunding from "../../../validations/transferFunding";
import { BankAccount } from "hooks/useGetBankAccounts";
import PaymentPaymentTypes from "enums/PaymentPaymentTypes";
import { banksOnConnection, keepOrPick } from "utils/accounts/moneyMovement";
import { formatCashFull } from "utils/formatCash";
import {
  SENT_PAYMENT_METHODS,
  paymentMethodLabelKey,
} from "utils/payments/paymentMethod";
import { Stack } from "components/layout/reusableLayouts";
import Summary from "components/common/Summary";
import {
  AccountSelect,
  AmountField,
  ConfirmTransfer,
  MemoField,
  TransferAccountsRow,
} from "./TransferFormFields";

interface FormikTransferFundsDrawerProps {
  fromAccount: string;
  toAccount: string;
  amount: string;
  memo: string;
  rail: PaymentPaymentTypes;
  confirmTransfer: boolean;
}

interface TransferFundsDrawerProps {
  open: boolean;
  handleCloseDrawer: (isReload: boolean) => void;
  /** The account the menu was opened from, preferred over the first one. */
  selectedFromAccountId?: string;
  fromAccountsData: BankAccount[];
  toAccountsData: BankAccount[];
  refetchAccounts: () => void;
}

const TransferFundsDrawer: React.FC<TransferFundsDrawerProps> = (props) => {
  const {
    open,
    handleCloseDrawer,
    selectedFromAccountId,
    fromAccountsData,
    toAccountsData,
    refetchAccounts,
  } = props;

  const { t } = useTranslation();
  const [bankLinkActive, setBankLinkActive] = useState(false);
  // One key per opening, so a retry after a lost response is the same wire
  const [requestId, setRequestId] = useState<string>(() => uuidv4());

  const [transferFunds, { loading: loadingTransfer }] = useMutation(
    TRANSFER_FUNDS,
    {
      onCompleted: () => {
        toast.success(t(isWire ? "WireSent" : "Transfer created!"));
        handleCloseDrawer(true);
        formik.resetForm();
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  );

  const formik = useFormik<FormikTransferFundsDrawerProps>({
    initialValues: {
      fromAccount: "",
      toAccount: "",
      amount: "",
      memo: "",
      rail: PaymentPaymentTypes.ACH,
      confirmTransfer: false,
    },
    validationSchema: transferFunding,
    onSubmit: async (values) => {
      const { fromAccount, toAccount, amount, memo } = values;

      await transferFunds({
        variables: {
          input: {
            fromAccountId: fromAccount,
            toAccountId: toAccount,
            amount: Number(amount),
            memo: memo,
            ...(isWire && {
              rail: PaymentPaymentTypes.WIRE,
              idempotencyKey: requestId,
            }),
          },
        },
      });
    },
  });

  useEffect(() => {
    formik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keyed on opening, not on the field being empty: the drawer is mounted
  // before any row is clicked, so filling it once would lock it to the first
  // account for good.
  useEffect(() => {
    if (!open || !fromAccountsData?.length) return;
    const preferred = fromAccountsData.find(
      (account: BankAccount) => account.id === selectedFromAccountId,
    );
    formik.setFieldValue("fromAccount", (preferred ?? fromAccountsData[0]).id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedFromAccountId, fromAccountsData?.[0]?.id]);

  useEffect(() => {
    if (open) setRequestId(uuidv4());
  }, [open]);

  const fromProgram = fromAccountsData.find(
    ({ id }) => id === formik.values.fromAccount,
  );
  const capabilities = fromProgram?.cardProgram?.capabilities;
  const canWire = Boolean(capabilities?.sendWire);
  const isWire = canWire && formik.values.rail === PaymentPaymentTypes.WIRE;
  const wireFee = capabilities?.wireFee ?? 0;
  const toBanks = useMemo(
    () => banksOnConnection(toAccountsData, fromProgram),
    [toAccountsData, fromProgram],
  );

  useEffect(() => {
    if (!open) return;
    const next = keepOrPick(toBanks, formik.values.toAccount);
    if (next !== formik.values.toAccount)
      formik.setFieldValue("toAccount", next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, toBanks, formik.values.toAccount]);

  const handleClose = () => {
    if (loadingTransfer) return;
    formik.resetForm();
    handleCloseDrawer(false);
  };

  const errorFor = (field: keyof FormikTransferFundsDrawerProps) =>
    formik.touched[field] ? (formik.errors[field] as string) : null;

  return (
    <Drawer
      open={open}
      disableEnforceFocus={bankLinkActive}
      onClose={handleClose}
      title={t("Transfer Funds")}
      width="544px"
      loading={loadingTransfer}
      footer={
        <>
          <Button variant="primary-outlined" onClick={handleClose}>
            {t("Cancel")}
          </Button>

          <Button
            variant="primary-filled"
            // No `isValid` gate: `handleSubmit` validates, touches every field
            // so the errors paint, and refuses to submit an invalid form.
            disabled={!formik.values.confirmTransfer || loadingTransfer}
            onClick={() => formik.handleSubmit()}
          >
            {loadingTransfer ? t("Transferring") : t("Complete")}
          </Button>
        </>
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <Stack gap="var(--space-6)">
          <TransferAccountsRow
            from={
              <AccountSelect
                name="fromAccount"
                ariaLabel={t("From")}
                value={formik.values.fromAccount}
                accounts={fromAccountsData}
                onSelect={(id) => formik.setFieldValue("fromAccount", id)}
                errorText={errorFor("fromAccount")}
              />
            }
            to={
              <AccountSelect
                name="toAccount"
                ariaLabel={t("To")}
                value={formik.values.toAccount}
                accounts={toBanks}
                onSelect={(id) => formik.setFieldValue("toAccount", id)}
                errorText={errorFor("toAccount")}
                // The old copy wrote the new account id to a `fundingAccount`
                // field this form does not have, so linking a bank here never
                // selected it.
                onAccountAdded={(id) => formik.setFieldValue("toAccount", id)}
                onBankLinkActiveChange={setBankLinkActive}
                refetchAccounts={refetchAccounts}
                program={fromProgram?.cardProgram}
              />
            }
          />

          {canWire && (
            <Select
              label={t("Transfer method")}
              value={formik.values.rail}
              onChange={(rail) =>
                formik.setFieldValue("rail", rail ?? PaymentPaymentTypes.ACH)
              }
              options={SENT_PAYMENT_METHODS.map((method) => ({
                id: method,
                label: t(paymentMethodLabelKey(method) || method),
              }))}
              required
            />
          )}

          <AmountField
            value={formik.values.amount}
            onChange={(raw) => formik.setFieldValue("amount", raw)}
            helperText={t(
              isWire
                ? "WireArrivalHelp"
                : "External transfers take 3-5 business days",
            )}
            errorText={errorFor("amount")}
          />

          {isWire && (
            <Summary
              items={[
                {
                  title: "Wire fee",
                  value: formatCashFull(wireFee, fromProgram?.currency),
                },
                {
                  title: "Total withdrawn",
                  value: formatCashFull(
                    Number(formik.values.amount || 0) + wireFee,
                    fromProgram?.currency,
                  ),
                },
              ]}
            />
          )}

          <MemoField
            value={formik.values.memo}
            onChange={(memo) => formik.setFieldValue("memo", memo)}
          />

          <ConfirmTransfer
            checked={formik.values.confirmTransfer}
            onChange={(checked) =>
              formik.setFieldValue("confirmTransfer", checked)
            }
            errorText={errorFor("confirmTransfer")}
          />
        </Stack>
      </form>
    </Drawer>
  );
};

export default TransferFundsDrawer;
